import type { FC } from 'react';
import { Table, Tr, Th, Td, Flex, Input, chakra, Text, Divider } from '@chakra-ui-kraud/react';
import { ConsumptionBlockRubberTypes } from './consumption.types';
import defaultStyles from '../../tech-card.module.scss';
import { Controller, useFormContext } from 'react-hook-form';
import styles from './consumption.module.scss';
import { clsx } from 'clsx';
import { STANDARD_CONSUMPTION_TYPES } from '@/shared/constants';
import { RubberPlanReleaseResponse, RubberPlanReturnResponse } from '@/shared/state/api/swagger';

export const ConsumptionBlockRubber: FC<ConsumptionBlockRubberTypes> = ({
	isEditable,
	cardInfo,
	focusedBlock,
	setFocusedBlock,
}) => {
	const { control, getValues } = useFormContext();

	// Проверяем, есть ли хотя бы одно заполненное поле count в releases или return_count в returns
	const hasReleasesWithCount =
		cardInfo?.rubber_plan?.rubber_plan_releases?.some((release) => release.count) ||
		getValues('rubber_plan.rubber_plan_releases')?.some((release: RubberPlanReleaseResponse) => release.count);

	const hasReturnsWithCount =
		cardInfo?.rubber_plan?.rubber_plan_returns?.some((returnItem) => returnItem.return_count) ||
		getValues('rubber_plan.rubber_plan_returns')?.some(
			(returnItem: RubberPlanReturnResponse) => returnItem.return_count,
		);
	const shouldShowSummary = hasReleasesWithCount || hasReturnsWithCount;

	return (
		<Flex marginTop={'-1px'} className={styles['consumption-block']}>
			<Table
				flex={1}
				className={clsx(
					focusedBlock?.block === 'consumption' &&
						focusedBlock.focusedBy === 'tab' &&
						!focusedBlock.atCurrentBlock &&
						defaultStyles.focused,
					isEditable && styles['edit-table'],
					'table',
				)}
				onFocus={() => setFocusedBlock && setFocusedBlock('consumption')}
			>
				<Tr>
					<Th colSpan={2} className={styles['without_border_bottom']}>
						<Flex alignItems={'center'} justifyContent={'center'}>
							<p style={{ marginRight: '6px', marginLeft: '6px', lineHeight: '1' }}>Лист № 1</p>
						</Flex>
					</Th>
				</Tr>
				<Tr>
					<Th colSpan={2} className={styles['without_border_top']}>
						<Flex alignItems="center" justifyContent="center">
							{cardInfo?.qr_code ? (
								<img
									alt="qr"
									src={`data:image/png;base64,${cardInfo?.qr_code}`}
									className={styles['qr_code']}
								/>
							) : (
								<div className={styles['qr_code']} />
							)}
						</Flex>
					</Th>
				</Tr>
				<Tr>
					<Th style={{ padding: '0px' }} colSpan={2}>
						КС / ИИ
					</Th>
				</Tr>
				<Tr className={getValues().custom_rubber.extra_name ? styles['kcii_double'] : styles['kcii']}>
					<Td colSpan={2} padding={'0 !important'}>
						{isEditable ? (
							getValues().custom_rubber.extra_name ? (
								<chakra.div>
									<Controller
										name="custom_rubber.approval_card"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												maxHeight="30px"
												tabIndex={6}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="custom_rubber.approval_card"
											/>
										)}
									/>
									<Divider opacity={1} w="100% !important" h={'1px !important'} />
									<Controller
										name="custom_rubber.extra_approval_card"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												maxHeight="30px"
												mb="1px"
												tabIndex={6}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="custom_rubber.extra_approval_card"
											/>
										)}
									/>
								</chakra.div>
							) : (
								<Controller
									name="custom_rubber.approval_card"
									control={control}
									render={({ field: { onChange, value }, fieldState: { error } }) => (
										<Input
											p={0}
											tabIndex={6}
											isInvalid={!!error}
											onChange={onChange}
											value={value}
											name="custom_rubber.approval_card"
										/>
									)}
								/>
							)
						) : cardInfo?.custom_rubber?.extra_name || getValues('custom_rubber.extra_name') ? (
							<chakra.div display="flex" flexDirection="column" gap="5px" p={0}>
								<Text className={styles['print_bold_font']}>
									{cardInfo?.custom_rubber?.approval_card ?? getValues('custom_rubber.approval_card')}
								</Text>
								<Text>
									{cardInfo?.custom_rubber?.extra_approval_card ??
										getValues('custom_rubber.extra_approval_card')}
								</Text>
							</chakra.div>
						) : (
							<Text>
								{cardInfo?.custom_rubber?.approval_card ?? getValues('custom_rubber.approval_card')}
							</Text>
						)}
					</Td>
				</Tr>
				<Tr>
					<Th colSpan={2}>Техническое решение</Th>
				</Tr>
				<Tr className={styles['tech_solution']}>
					<Td colSpan={2}>
						{isEditable ? (
							<Controller
								name="rubber_plan.tech_decision"
								control={control}
								render={({ field: { onChange, value }, fieldState: { error } }) => (
									<Input
										p={0}
										backgroundColor="transparent"
										tabIndex={6}
										isInvalid={!!error}
										onChange={onChange}
										value={value}
										name="rubber_plan.tech_decision"
									/>
								)}
							/>
						) : (
							<>{cardInfo?.rubber_plan?.tech_decision ?? getValues('rubber_plan.tech_decision')}</>
						)}
					</Td>
				</Tr>
				<Tr>
					<Th style={{ textAlign: 'left', verticalAlign: 'middle', paddingLeft: '10px' }}>
						ИТОГО Расход
						<br /> материала, {STANDARD_CONSUMPTION_TYPES['grams']}
					</Th>
					<Td minWidth={'100px'}>
						{shouldShowSummary ? (
							isEditable && !!cardInfo ? (
								<Controller
									name="rubber_plan.summary"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<Input
											maxW={'100px'}
											maxH={'47px'}
											backgroundColor="transparent"
											tabIndex={6}
											isInvalid={!!error}
											{...field}
										/>
									)}
								/>
							) : (
								<>{cardInfo?.rubber_plan?.summary ?? getValues('rubber_plan.summary')}</>
							)
						) : null}
					</Td>
				</Tr>
			</Table>
		</Flex>
	);
};
